"use client";

import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  CheckSquare2,
  Clock3,
  FileText,
  FolderKanban,
  ListTodo,
  Radio,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "@/lib/store";
import {
  homeAttentionSummary,
  relevantHomeDocs,
  relevantHomeProjects,
  relevantHomeTasks,
} from "@/lib/workspaceHome";
import { HomeSignalField } from "./home/HomeSignalField";
import { liveRunFamilySignal, rootRuns } from "@/lib/liveAgentRuns";
import { cn } from "@/lib/utils";

export function WorkspaceHome({
  onNavigate,
  onOpenCommand,
  focusSlot,
}: {
  onNavigate: (view: string) => void;
  onOpenCommand: () => void;
  focusSlot?: ReactNode;
}) {
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);
  const currentUserId = useStore((state) => state.currentUserId);
  const issues = useStore((state) => state.issues);
  const projects = useStore((state) => state.projects);
  const docs = useStore((state) => state.docs);
  const agentRuns = useStore((state) => state.agentRuns);
  const [signalNow, setSignalNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setSignalNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const workspaceTasks = useMemo(
    () => issues.filter((issue) => issue.workspaceId === activeWorkspaceId),
    [issues, activeWorkspaceId]
  );
  const workspaceProjects = useMemo(
    () => projects.filter((project) => project.workspaceId === activeWorkspaceId),
    [projects, activeWorkspaceId]
  );
  const workspaceDocs = useMemo(
    () => docs.filter((doc) => doc.workspaceId === activeWorkspaceId),
    [docs, activeWorkspaceId]
  );

  const taskItems = useMemo(
    () => relevantHomeTasks(workspaceTasks, currentUserId),
    [workspaceTasks, currentUserId]
  );
  const attention = useMemo(
    () => homeAttentionSummary(workspaceTasks, currentUserId),
    [workspaceTasks, currentUserId]
  );
  const projectItems = useMemo(
    () => relevantHomeProjects(workspaceProjects, workspaceTasks, currentUserId),
    [workspaceProjects, workspaceTasks, currentUserId]
  );
  const relevantProjectIds = useMemo(
    () => new Set(projectItems.map((project) => project.id)),
    [projectItems]
  );
  const docItems = useMemo(
    () => relevantHomeDocs(workspaceDocs, relevantProjectIds, currentUserId),
    [workspaceDocs, relevantProjectIds, currentUserId]
  );

  const hasAttention = attention.awaitingReview > 0 || attention.assigned > 0;
  const liveRuns = useMemo(
    () =>
      rootRuns(
        agentRuns.filter(
          (run) =>
            run.workspaceId === activeWorkspaceId &&
            run.status === "in_progress"
        )
      ),
    [activeWorkspaceId, agentRuns]
  );
  const recentLiveRuns = liveRuns.filter((run) => liveRunFamilySignal(run, agentRuns, signalNow) !== "stale");
  const hasFreshLiveRun = recentLiveRuns.length > 0;

  return (
    <div className="relative flex-1 overflow-y-auto scrollbar-thin">
      <HomeSignalField />

      <div className="relative z-10 mx-auto max-w-[1120px] px-5 py-10 md:px-8 md:py-14">
        <div className="mb-8 max-w-[680px]">
          <h2 className="text-[28px] leading-[34px] font-semibold tracking-[-0.02em]">
            What do you need?
          </h2>
          <p className="mt-2 text-[14px] leading-6 text-[var(--fg-tertiary)]">
            Review what needs you, find context, or see where work stands.
          </p>

          <button
            type="button"
            onClick={onOpenCommand}
            className="focus-ring mt-6 flex h-11 w-full items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] px-4 text-left text-[13px] text-[var(--fg-tertiary)] transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
          >
            <Search size={15} className="shrink-0 text-[var(--fg-muted)]" />
            <span className="min-w-0 flex-1 truncate sm:hidden">
              Search or run a command…
            </span>
            <span className="hidden min-w-0 flex-1 truncate sm:block">
              Search tasks, docs and projects, or run a command…
            </span>
            <span className="hidden shrink-0 sm:inline-flex">
              <kbd className="kbd">⌘ K</kbd>
            </span>
          </button>

          <div className="mt-4 flex min-h-7 flex-wrap items-center gap-2">
            <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-[var(--fg-muted)]">
              Precisa de você
            </span>
            {hasAttention ? (
              <>
                {attention.awaitingReview > 0 && (
                  <FocusLink
                    href="#awaiting-me"
                    icon={Clock3}
                    label={`${attention.awaitingReview} para sua revisão/decisão`}
                    onNavigate={() => onNavigate("awaiting-me")}
                  />
                )}
                {attention.assigned > 0 && (
                  <FocusLink
                    href="#my-issues"
                    icon={ListTodo}
                    label={`${attention.assigned} open ${attention.assigned === 1 ? "task" : "tasks"}`}
                    onNavigate={() => onNavigate("my-issues")}
                  />
                )}
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--fg-tertiary)]">
                Veja suas tarefas e revisões no workspace
              </span>
            )}
          </div>
        </div>

        {focusSlot}

        <div className="mb-5 flex flex-wrap gap-2">
          <FocusLink href="#pending" icon={ListTodo} label="Revisar pendências" onNavigate={() => onNavigate("pending")} />
          <FocusLink href="?task_nav=inbox#inbox" icon={Clock3} label="Inbox e preferências" onNavigate={() => onNavigate("inbox")} />
        </div>


        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <HomeCard
            href="#projects"
            icon={FolderKanban}
            accent="var(--brand-indigo)"
            title="Projects"
            description="See where work is headed"
            count={projectItems.length}
            countLabel={projectItems.length === 1 ? "active project" : "active projects"}
            items={projectItems.slice(0, 3).map((project) => project.name)}
            empty="No active projects yet"
            onNavigate={() => onNavigate("projects")}
          />
          <HomeCard
            href="#docs"
            icon={FileText}
            accent="var(--purple)"
            title="Docs"
            description="Find decisions and context"
            count={workspaceDocs.length}
            countLabel={workspaceDocs.length === 1 ? "document" : "documents"}
            items={docItems.slice(0, 3).map((doc) => doc.title)}
            empty="No documents yet"
            onNavigate={() => onNavigate("docs")}
          />
          <HomeCard
            href="#tasks"
            icon={CheckSquare2}
            accent="var(--brand-indigo)"
            title="Tasks"
            description="Handle what needs your attention"
            count={taskItems.length}
            countLabel={taskItems.length === 1 ? "relevant task" : "relevant tasks"}
            items={taskItems.slice(0, 3).map((task) => `${task.identifier} · ${task.title}`)}
            empty="Nothing needs your attention"
            onNavigate={() => onNavigate("tasks")}
          />
        </div>
        <details className="mt-5 rounded-lg border border-[var(--border)] p-3"><summary className="focus-ring min-h-11 cursor-pointer content-center text-sm text-[var(--fg-secondary)]">Operação</summary>
        <button
          type="button"
          onClick={() => onNavigate("live")}
          className="focus-ring mb-4 flex w-full items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] px-4 py-3 text-left transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
        >
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--accent)]">
            {liveRuns.length > 0 && (
              <span
                className={cn(
                  "absolute right-1 top-1 h-1.5 w-1.5 rounded-full",
                  hasFreshLiveRun
                    ? "animate-pulse bg-[var(--green)]"
                    : "bg-[var(--red)]"
                )}
              />
            )}
            <Radio size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[12.5px] font-medium text-[var(--fg)]">
              {recentLiveRuns.length > 0
                ? `${recentLiveRuns.length} ${recentLiveRuns.length === 1 ? "execução com sinal recente" : "execuções com sinal recente"}`
                : liveRuns.length > 0 ? "Execuções sem sinal recente" : "Live workspace"}
            </span>
            <span className="mt-0.5 block truncate text-[11.5px] text-[var(--fg-tertiary)]">
              {recentLiveRuns[0]
                ? recentLiveRuns[0].currentStep || recentLiveRuns[0].prompt
                : liveRuns.length > 0 ? "Resultado não confirmado. Abra Live para consultar o histórico."
                : "See current steps, subagents, and recent outcomes"}
            </span>
          </span>
          <ArrowRight size={14} className="shrink-0 text-[var(--fg-muted)]" />
        </button>

        </details>
      </div>
    </div>
  );
}

function FocusLink({
  href,
  icon: Icon,
  label,
  onNavigate,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  onNavigate: () => void;
}) {
  return (
    <a
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        onNavigate();
      }}
      className="focus-ring inline-flex min-h-11 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2 py-1 text-[12px] font-medium text-[var(--fg-secondary)] no-underline transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
    >
      <Icon size={13} className="text-[var(--fg-muted)]" />
      {label}
    </a>
  );
}

function HomeCard({
  href,
  icon: Icon,
  accent,
  title,
  description,
  count,
  countLabel,
  items,
  empty,
  onNavigate,
}: {
  href: string;
  icon: LucideIcon;
  accent: string;
  title: string;
  description: string;
  count: number;
  countLabel: string;
  items: string[];
  empty: string;
  onNavigate: () => void;
}) {
  function updateSpotlight(event: ReactPointerEvent<HTMLAnchorElement>) {
    if (event.pointerType !== "mouse") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      event.currentTarget.style.setProperty("--home-spot-y", "-240px");
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--home-spot-x", `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty("--home-spot-y", `${event.clientY - rect.top}px`);
  }

  return (
    <a
      href={href}
      data-home-card={title.toLowerCase()}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        onNavigate();
      }}
      onPointerMove={updateSpotlight}
      onPointerLeave={(event) => {
        event.currentTarget.style.setProperty("--home-spot-y", "-240px");
      }}
      style={
        {
          "--home-card-accent": accent,
          "--home-spot-x": "50%",
          "--home-spot-y": "-240px",
        } as CSSProperties
      }
      className="group relative min-h-[300px] overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-5 text-inherit no-underline transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] focus-ring"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(240px circle at var(--home-spot-x) var(--home-spot-y), color-mix(in srgb, var(--home-card-accent) 10%, transparent), transparent 72%)",
        }}
      />

      <div className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-secondary)] transition-colors group-hover:border-[var(--home-card-accent)] group-hover:text-[var(--home-card-accent)]">
            <Icon size={19} />
          </div>
          <ArrowRight
            size={16}
            className="mt-1 text-[var(--fg-muted)] transition-colors group-hover:text-[var(--home-card-accent)]"
          />
        </div>
        <h3 className="mt-5 text-[18px] font-semibold tracking-[-0.01em]">{title}</h3>
        <p className="mt-1 text-[13px] leading-5 text-[var(--fg-tertiary)]">{description}</p>
        <div className="mt-5 border-t border-[var(--border)] pt-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-[var(--fg-muted)]">
            {count} {countLabel}
          </div>
          <div className="mt-3 space-y-2">
            {items.length > 0 ? (
              items.map((item) => (
                <div
                  key={item}
                  className="truncate text-[12.5px] leading-5 text-[var(--fg-secondary)]"
                >
                  {item}
                </div>
              ))
            ) : (
              <div className="text-[12.5px] leading-5 text-[var(--fg-muted)]">{empty}</div>
            )}
          </div>
        </div>
      </div>
    </a>
  );
}
